import { useState, useCallback } from "react";
import clsx from "clsx";
import {
  CalendarDaysIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import api from "../../services/api";
import callService from "../../services/callService";
import toast from "react-hot-toast";

/**
 * Minimal wrap-up for front-desk confirmation calls only.
 * Agent / lead-queue / power dialer continue to use {@link WrapUpModal}.
 */
const FrontDeskWrapUpModal = ({
  isOpen,
  onComplete,
  lead,
  twilioCallSid,
}) => {
  const [saving, setSaving] = useState(false);

  const resolvedAppointmentId =
    lead?.appointmentId != null && String(lead.appointmentId).trim() !== ""
      ? String(lead.appointmentId).trim()
      : "";
  const leadPhone = lead?.phone || lead?.contact?.phone;
  const apptLine = [lead?.appointmentTime, lead?.appointmentType]
    .filter(Boolean)
    .join(" · ");

  const persistCallOutcome = useCallback(
    async (outcome, notes) => {
      if (!twilioCallSid) return;
      await callService.updateCallOutcome(twilioCallSid, {
        outcome,
        notes,
      });
    },
    [twilioCallSid],
  );

  const notifyDashboard = useCallback(() => {
    window.dispatchEvent(new CustomEvent("chairside:frontdesk-appointments-changed"));
  }, []);

  const handleMarkConfirmed = async () => {
    if (saving) return;
    setSaving(true);
    try {
      if (resolvedAppointmentId) {
        await api.put(`/appointments/${resolvedAppointmentId}/status`, {
          status: "confirmed",
        });
      }
      await persistCallOutcome(
        "connected",
        "Front desk: patient confirmed they will attend today’s appointment.",
      );
      notifyDashboard();
      toast.success("Appointment marked confirmed");
      onComplete?.();
    } catch (err) {
      toast.error(
        err?.error?.message ||
          err?.message ||
          "Could not update appointment or call log",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleMarkCancelled = async () => {
    if (saving) return;
    setSaving(true);
    try {
      if (resolvedAppointmentId) {
        await api.put(`/appointments/${resolvedAppointmentId}/status`, {
          status: "cancelled",
        });
      }
      await persistCallOutcome(
        "other",
        "Front desk: appointment cancelled after confirmation call.",
      );
      notifyDashboard();
      toast.success("Appointment marked cancelled");
      onComplete?.();
    } catch (err) {
      toast.error(
        err?.error?.message ||
          err?.message ||
          "Could not update appointment or call log",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCallOnly = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await persistCallOutcome(
        "connected",
        "Front desk: call completed (no appointment linked to this row).",
      );
      notifyDashboard();
      toast.success("Call saved");
      onComplete?.();
    } catch (err) {
      toast.error(err?.error?.message || err?.message || "Could not save call");
    } finally {
      setSaving(false);
    }
  };

  /** Keeps appointment as-is so the patient stays on the confirmation call list. */
  const handleCallAgainLater = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await persistCallOutcome(
        "follow_up",
        "Front desk: will call again later — appointment left unchanged.",
      );
      toast.success("Saved — patient stays on your call list");
      onComplete?.();
    } catch (err) {
      toast.error(err?.error?.message || err?.message || "Could not save call log");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-[2px] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-200 bg-gradient-to-r from-orange-500 to-orange-600 text-white shrink-0">
          <div>
            <h3 className="text-lg font-semibold">Call wrap-up</h3>
            <p className="text-sm text-white/90">
              {lead?.firstName} {lead?.lastName}
              {leadPhone && (
                <span className="ml-2 font-mono text-xs bg-white/20 px-1.5 py-0.5 rounded">
                  {leadPhone}
                </span>
              )}
            </p>
          </div>
        </div>

        <div className="p-6 space-y-5">
          <div className="flex gap-3 rounded-xl border border-amber-100 bg-amber-50/80 p-3 text-sm text-amber-950">
            <CalendarDaysIcon className="w-5 h-5 shrink-0 text-amber-600" />
            <div>
              <p className="font-semibold text-amber-900">
                Today&apos;s appointment
              </p>
              {apptLine ? (
                <p className="mt-1 text-xs text-amber-800">{apptLine}</p>
              ) : null}
              <p className="mt-2 text-xs text-surface-700 leading-relaxed">
                Did the patient confirm they are coming in for this appointment?
              </p>
            </div>
          </div>

          {resolvedAppointmentId ? (
            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              <button
                type="button"
                disabled={saving}
                onClick={handleMarkConfirmed}
                className={clsx(
                  "inline-flex min-h-[2.75rem] min-w-0 flex-1 flex-row items-center justify-center gap-1 rounded-xl border-2 border-success-300 bg-success-50 px-1 py-2.5 text-[10px] font-semibold leading-tight text-success-900 shadow-sm transition-colors sm:gap-1.5 sm:px-2 sm:text-xs",
                  "hover:bg-success-100 disabled:opacity-50 disabled:pointer-events-none",
                )}
              >
                <CheckCircleIcon className="h-4 w-4 shrink-0 sm:h-5 sm:w-5" />
                <span className="text-center leading-tight">Mark confirmed</span>
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={handleMarkCancelled}
                className={clsx(
                  "inline-flex min-h-[2.75rem] min-w-0 flex-1 flex-row items-center justify-center gap-1 rounded-xl border-2 border-rose-300 bg-rose-50 px-1 py-2.5 text-[10px] font-semibold leading-tight text-rose-900 shadow-sm transition-colors sm:gap-1.5 sm:px-2 sm:text-xs",
                  "hover:bg-rose-100 disabled:opacity-50 disabled:pointer-events-none",
                )}
              >
                <XCircleIcon className="h-4 w-4 shrink-0 sm:h-5 sm:w-5" />
                <span className="text-center leading-tight">Cancelled</span>
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={handleCallAgainLater}
                className={clsx(
                  "inline-flex min-h-[2.75rem] min-w-0 flex-1 flex-row items-center justify-center gap-1 rounded-xl border border-surface-200 bg-white px-1 py-2.5 text-[10px] font-semibold leading-tight text-surface-800 shadow-sm transition-colors sm:gap-1.5 sm:px-2 sm:text-xs",
                  "hover:bg-surface-50 disabled:opacity-50 disabled:pointer-events-none",
                )}
              >
                <ArrowPathIcon className="h-4 w-4 shrink-0 text-amber-600 sm:h-5 sm:w-5" />
                <span className="text-center leading-tight">Call again later</span>
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-surface-600 leading-relaxed">
                This call isn&apos;t linked to a confirmation appointment in the
                list. You can still save the call to history and close.
              </p>
              <button
                type="button"
                disabled={saving}
                onClick={handleSaveCallOnly}
                className="w-full rounded-xl border border-surface-200 bg-surface-50 px-4 py-3 text-sm font-semibold text-surface-800 hover:bg-surface-100 disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save & close"}
              </button>
            </div>
          )}

          {resolvedAppointmentId ? (
            <p className="text-[11px] text-center text-surface-500 leading-relaxed">
              {saving
                ? "Saving…"
                : "Mark confirmed / cancelled updates the appointment. Call again later only updates the call log — the patient stays on your list."}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default FrontDeskWrapUpModal;
